import { type DocumentRepository, RevisionConflictError, type SaveOptions } from '@/application';
import { type SceneDocument } from '@/domain/model';

/**
 * Debounced autosave with backoff.
 *
 * ## Why debounce rather than save on every edit
 *
 * A drag emits an edit per pointer-move. Writing the document each time would serialise
 * a whole construction sixty times a second and, on a real backend, issue sixty requests
 * for one gesture. The debounce collapses a burst into one write once the user pauses.
 *
 * ## Why a periodic flush as well
 *
 * A user who keeps editing without ever pausing would, with debounce alone, never
 * trigger a save at all. `maxDelay` guarantees a write at least that often no matter how
 * continuous the activity.
 *
 * Timers are injected for the same reason the render scheduler injects
 * `requestAnimationFrame`: an autosave that can only be tested by waiting two real
 * seconds is an autosave nobody tests.
 */

export interface Timers {
  setTimeout(handler: () => void, ms: number): number;
  clearTimeout(handle: number): void;
  now(): number;
}

export const systemTimers: Timers = {
  setTimeout: (handler, ms) => globalThis.setTimeout(handler, ms),
  clearTimeout: (handle) => {
    globalThis.clearTimeout(handle);
  },
  now: () => Date.now(),
};

export const AutosaveStatus = {
  Idle: 'idle',
  Pending: 'pending',
  Saving: 'saving',
  Saved: 'saved',
  Failed: 'failed',
  Conflict: 'conflict',
} as const;
export type AutosaveStatus = (typeof AutosaveStatus)[keyof typeof AutosaveStatus];

export interface AutosaveState {
  readonly status: AutosaveStatus;
  readonly lastSavedAt: number | null;
  readonly error: string | null;
}

export interface AutosaveOptions {
  /** Quiet period before a save fires. */
  readonly delay?: number;
  /** Longest a save may be deferred while edits keep arriving. */
  readonly maxDelay?: number;
  readonly timers?: Timers;
  /** Retry delays after a failure, in order. The last is reused for further attempts. */
  readonly backoff?: readonly number[];
}

const DEFAULTS = {
  delay: 800,
  maxDelay: 5000,
  backoff: [1000, 3000, 10_000],
};

export class Autosave {
  private readonly repository: DocumentRepository;
  private readonly documentId: string;
  private readonly timers: Timers;
  private readonly delay: number;
  private readonly maxDelay: number;
  private readonly backoff: readonly number[];

  private handle: number | null = null;
  private pending: SceneDocument | null = null;
  private firstPendingAt: number | null = null;
  private inFlight = false;
  private failures = 0;

  private revision: string | null = null;
  private unknown: Readonly<Record<string, unknown>> = {};

  private state: AutosaveState = { status: AutosaveStatus.Idle, lastSavedAt: null, error: null };
  private listeners: ((state: AutosaveState) => void)[] = [];

  constructor(repository: DocumentRepository, documentId: string, options: AutosaveOptions = {}) {
    this.repository = repository;
    this.documentId = documentId;
    this.timers = options.timers ?? systemTimers;
    this.delay = options.delay ?? DEFAULTS.delay;
    this.maxDelay = options.maxDelay ?? DEFAULTS.maxDelay;
    this.backoff = options.backoff ?? DEFAULTS.backoff;
  }

  get current(): AutosaveState {
    return this.state;
  }

  /** Adopts the revision and preserved fields from a freshly loaded document. */
  adopt(revision: string | null, unknown: Readonly<Record<string, unknown>> = {}): void {
    this.revision = revision;
    this.unknown = unknown;
  }

  subscribe(listener: (state: AutosaveState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** Notes that the document changed. Schedules a save. */
  schedule(document: SceneDocument): void {
    this.pending = document;
    this.firstPendingAt ??= this.timers.now();
    this.setState({ status: AutosaveStatus.Pending, error: null });

    if (this.handle !== null) this.timers.clearTimeout(this.handle);

    const waited = this.timers.now() - this.firstPendingAt;
    const remaining = Math.max(0, this.maxDelay - waited);
    this.handle = this.timers.setTimeout(
      () => {
        void this.flush();
      },
      Math.min(this.delay, remaining)
    );
  }

  /** Writes immediately, whatever the timers say. Used on unload and on explicit save. */
  async flush(): Promise<void> {
    if (this.handle !== null) {
      this.timers.clearTimeout(this.handle);
      this.handle = null;
    }

    const document = this.pending;
    if (document === null || this.inFlight) return;

    this.pending = null;
    this.firstPendingAt = null;
    this.inFlight = true;
    this.setState({ status: AutosaveStatus.Saving, error: null });

    const options: SaveOptions = { expectedRevision: this.revision, unknown: this.unknown };

    try {
      const summary = await this.repository.save(this.documentId, document, options);
      this.revision = summary.revision;
      this.failures = 0;
      this.setState({
        status: AutosaveStatus.Saved,
        lastSavedAt: summary.updatedAt,
        error: null,
      });
    } catch (error) {
      // A conflict is not retried: another tab or user has newer work, and writing over
      // it is precisely what the check exists to prevent. The UI has to ask.
      if (error instanceof RevisionConflictError) {
        this.pending = document;
        this.setState({
          status: AutosaveStatus.Conflict,
          error: error.message,
        });
      } else {
        this.failures += 1;
        this.pending = document;
        this.setState({
          status: AutosaveStatus.Failed,
          error: error instanceof Error ? error.message : String(error),
        });
        this.scheduleRetry();
      }
    } finally {
      this.inFlight = false;
    }
  }

  /** Stops any pending write. Does not cancel one already in flight. */
  dispose(): void {
    if (this.handle !== null) {
      this.timers.clearTimeout(this.handle);
      this.handle = null;
    }
    this.listeners = [];
  }

  private scheduleRetry(): void {
    const index = Math.min(this.failures - 1, this.backoff.length - 1);
    const wait = this.backoff[index] ?? this.backoff[this.backoff.length - 1] ?? 1000;
    this.handle = this.timers.setTimeout(() => {
      void this.flush();
    }, wait);
  }

  /**
   * Records the new state and tells anyone listening — but only when something changed.
   *
   * The guard is not a micro-optimisation. A listener here is a React store, so a notification
   * is a render; and a render can lead back to whatever caused the schedule. Announcing
   * "still pending" to a pending world was one link of a loop that ended in "Maximum update
   * depth exceeded", with a document that had not been edited anywhere in it.
   *
   * `lastSavedAt` is a number and `error` a string or null, so a shallow comparison is exact.
   */
  private setState(patch: Partial<AutosaveState>): void {
    const next = { ...this.state, ...patch };
    const same =
      next.status === this.state.status &&
      next.lastSavedAt === this.state.lastSavedAt &&
      next.error === this.state.error;
    if (same) return;

    this.state = next;
    for (const listener of this.listeners) listener(this.state);
  }
}
